import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buildLegacyFileUrl, getPresignedFileUrl } from '../../services/fileUrlService';
import { isInlineFileSrc, resolveLocalPreviewUrl } from '../../services/offlineAttachmentStore';
import { formatAudioDuration, probeAudioElementDuration, readUrlDurationSeconds } from '../../utils/fileUtils';

const isUsableDuration = (value, known) => {
  if (!Number.isFinite(value) || value < 0.5) return false;
  if (Number.isFinite(known) && known >= 1 && value < known * 0.5) return false;
  return true;
};

const PresignedAudio = ({
  fileKey,
  className = '',
  style,
  children,
  durationSeconds: durationHint,
  onDuration,
}) => {
  const [src, setSrc] = useState(null);
  const [legacyTried, setLegacyTried] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(
    Number.isFinite(Number(durationHint)) && Number(durationHint) > 0 ? Number(durationHint) : 0
  );
  const audioRef = useRef(null);
  const onDurationRef = useRef(onDuration);
  onDurationRef.current = onDuration;

  const applyDuration = useCallback((value) => {
    setDuration((prev) => {
      if (!isUsableDuration(value, prev)) return prev;
      if (value !== prev) onDurationRef.current?.(value);
      return value;
    });
  }, []);

  useEffect(() => {
    const next = Number(durationHint);
    if (!Number.isFinite(next) || next < 0.5) return;
    setDuration((prev) => (prev && prev >= 1 && next < prev * 0.5 ? prev : next));
  }, [durationHint]);

  useEffect(() => {
    let cancelled = false;
    setPlaying(false);
    setCurrentTime(0);
    if (!fileKey) {
      setSrc(null);
      setLegacyTried(false);
      return undefined;
    }
    if (isInlineFileSrc(fileKey)) {
      setSrc(fileKey);
      setLegacyTried(false);
      return undefined;
    }
    setLegacyTried(false);
    (async () => {
      const localUrl = await resolveLocalPreviewUrl(fileKey);
      if (localUrl) {
        if (!cancelled) setSrc(localUrl);
        return;
      }
      const url = await getPresignedFileUrl(fileKey);
      if (!cancelled) setSrc(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [fileKey]);

  useEffect(() => {
    if (!src) return undefined;
    const hint = Number(durationHint);
    if (Number.isFinite(hint) && hint >= 0.5) return undefined;
    let cancelled = false;
    readUrlDurationSeconds(src).then((value) => {
      if (!cancelled) applyDuration(value);
    });
    return () => {
      cancelled = true;
    };
  }, [src, durationHint, applyDuration]);

  const togglePlayback = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const audioEl = audioRef.current;
    if (!audioEl || !src) return;
    try {
      if (audioEl.paused) {
        await audioEl.play();
      } else {
        audioEl.pause();
      }
    } catch {
      // Autoplay or decode errors are ignored; user can retry.
    }
  };

  const seek = (event) => {
    const audioEl = audioRef.current;
    if (!audioEl || !duration) return;
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    audioEl.currentTime = next;
    setCurrentTime(next);
  };

  if (!fileKey) return null;

  if (!src) {
    return (
      <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-full h-10 ${className || ''}`} aria-hidden />
    );
  }

  const total = duration > 0 ? duration : 0;
  const progressMax = total > 0 ? total : Math.max(currentTime, 1);
  const currentLabel = formatAudioDuration(currentTime) || '0:00';
  const totalLabel = formatAudioDuration(total) || '0:00';

  return (
    <div className={`flex items-center gap-2 w-full rounded-full bg-gray-100 dark:bg-gray-700/80 px-2 py-1.5 ${className}`} style={style}>
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={(event) => {
          applyDuration(event.currentTarget.currentTime);
          setPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={(event) => {
          setCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onLoadedMetadata={(event) => {
          applyDuration(probeAudioElementDuration(event.currentTarget));
        }}
        onDurationChange={(event) => {
          applyDuration(probeAudioElementDuration(event.currentTarget));
        }}
        onError={() => {
          if (legacyTried) return;
          const legacyUrl = buildLegacyFileUrl(fileKey);
          if (!legacyUrl || legacyUrl === src) {
            setLegacyTried(true);
            return;
          }
          setLegacyTried(true);
          setSrc(legacyUrl);
        }}
      >
        {children}
      </audio>
      <button
        type="button"
        onClick={togglePlayback}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <input
        type="range"
        min={0}
        max={progressMax}
        step={0.05}
        value={Math.min(currentTime, progressMax)}
        onChange={seek}
        className="flex-1 min-w-0 accent-primary-600 h-1.5"
        aria-label="Seek"
      />
      <span className="flex-shrink-0 text-[11px] tabular-nums text-gray-600 dark:text-gray-300 whitespace-nowrap">
        {currentLabel} / {totalLabel}
      </span>
    </div>
  );
};

export default PresignedAudio;
